import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useMatch, NavLink } from "react-router-dom";
import SubNavItem from "../../types/nav/SubNavItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, HighlightDot } from "../common";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import NavIcon from "./NavIcon";

interface LeftSubNavGroupProps {
  isExpanded: boolean;
  subNavId: string;
  baseRoute: string;
  baseLinkText: string;
  items: SubNavItem[];
  highlightRootLevel?: boolean;
  onToggleExpand: (navItemId: string) => void;
}

function LeftSubNavGroup({
  isExpanded,
  subNavId,
  baseRoute,
  baseLinkText,
  items,
  highlightRootLevel = false,
  onToggleExpand,
}: LeftSubNavGroupProps) {
  const { t } = useTranslation();
  const isActive = useMatch({
    path: baseRoute,
    end: false,
  });

  const toggleSubNav = () => {
    onToggleExpand(subNavId);
  };

  const subItemIsActiveShouldMatchFullPath = (subItem: SubNavItem) => {
    return subItem.url.indexOf("/tasks/") > -1 ? false : true;
  };

  const toggleAriaLabel = isExpanded
    ? t("Nav.LargeScreen.CollapseSection", "Collapse {{section}}", {
        section: baseLinkText,
      })
    : t("Nav.LargeScreen.ExpandSection", "Expand {{section}}", {
        section: baseLinkText,
      });

  return (
    <div
      className={cx(
        "navigation-group",
        isActive ? "navigation-group-active" : ""
      )}
    >
      <div className="flex flex-row items-center group">
        <div className="flex-grow">
          <NavLink
            className={({ isActive: linkActive }) =>
              cx(
                "navigation-link",
                linkActive
                  ? "navigation-link-active"
                  : "navigation-link-inactive"
              )
            }
            to={baseRoute}
          >
            <span
              className={cx(
                "navigation-group-title",
                isActive ? "" : "group-hover:text-gray-800",
                isExpanded ? "font-medium text-gray-900" : ""
              )}
            >
              {baseLinkText}
            </span>
            {highlightRootLevel && <HighlightDot containerClassNames="" />}
          </NavLink>
        </div>
        <button
          type="button"
          className="navigation-subnav-toggle px-3 text-gray-500 group-hover:text-gray-600"
          onClick={toggleSubNav}
          aria-expanded={isExpanded}
          aria-controls={subNavId}
          aria-label={toggleAriaLabel}
        >
          <FontAwesomeIcon
            size="xs"
            icon={faChevronRight}
            className={cx(
              "navigation-subnav-chevron transform transition-transform duration-300",
              isExpanded ? "rotate-90" : ""
            )}
            aria-hidden
          />
        </button>
      </div>

      <div
        id={subNavId}
        {...(!isExpanded ? { "aria-hidden": true } : {})}
        className={cx(
          "navigation-group-items",
          isExpanded ? "expanded" : "collapsed"
        )}
      >
        {items.map((navItem) => (
          <NavLink
            className={({ isActive: linkActive }) =>
              cx(
                "navigation-group-item",
                linkActive
                  ? "navigation-group-item-active"
                  : "navigation-group-item-inactive"
              )
            }
            to={navItem.url}
            key={navItem.title}
            end={subItemIsActiveShouldMatchFullPath(navItem)}
            tabIndex={isExpanded ? undefined : -1}
          >
            {({ isActive: linkActive }) => (
              <>
                {navItem.icon && (
                  <NavIcon icon={navItem.icon} isSelected={linkActive} />
                )}
                <span className="navigation-item-text text-[14px]">
                  {navItem.translateTitle ? t(navItem.title) : navItem.title}
                </span>
                {navItem.badgeCount !== undefined && navItem.badgeCount > 0 && (
                  <Badge
                    text={navItem.badgeCount.toString()}
                    backgroundColourClassName={
                      linkActive ? "primary-bg" : "accent-bg"
                    }
                    textColourClassName="text-white"
                    marginClassName="ml-1.5"
                  />
                )}
              </>
            )}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default LeftSubNavGroup;
